/* global setInterval, clearInterval */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AppState } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ServiceModuleToast } from './ServiceModuleFeedback';
import i18n from '../../i18n';
import {haptics} from './haptics';
import {
    endLocalBreak,
    migrateLoadedTimer,
    pauseLocalTimer,
    resumeLocalTimer,
    startLocalBreak,
    startLocalTimer,
    stopLocalTimer,
    withLiveElapsed,
} from './localTimerModel';

const STORAGE_KEY = '@happy_time_tracking';
const TimeTrackingContext = createContext(null);

/**
 * DEVICE-LOCAL stopwatches — the dashboard's quick "coffee / lunch / travel /
 * custom" activities, which belong to no work order and so have no backend clock.
 * (Work-order timers are the backend's: see `helpers/useWorkOrderTimers`.)
 *
 * Timer states: idle | running | paused | break
 * Each timer tracks: worksheetId, taskId, taskName, status, startedAt,
 * totalElapsed (ms), breakElapsed (ms), segments: [{ type: 'work'|'break', start, end?, reason? }]
 *
 * NOTHING IS COUNTED PER TICK. `totalElapsed` / `breakElapsed` hold the BANKED
 * time of finished segments; the value consumers read is `banked + (now - the open
 * segment's start)`, derived from the wall clock on every render
 * (`localTimerModel.withLiveElapsed`). Android suspends JS timers while the phone
 * is locked — the old `+1000` per `setInterval` tick silently dropped every locked
 * minute; the wall clock keeps running, so a locked phone (or a restart) loses
 * nothing. The 1 s interval below only asks React to repaint.
 */

/** Idle (stopped) timers older than this are dropped when the store is loaded. */
const IDLE_TIMER_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

const isLive = (timer) => timer?.status === 'running' || timer?.status === 'break';

export const TimeTrackingProvider = ({ children }) => {
    const [timers, setTimers] = useState({});
    const [nowMs, setNowMs] = useState(() => Date.now());
    const loadedRef = useRef(false);

    // Load persisted timers on mount
    useEffect(() => {
        const load = async () => {
            try {
                const raw = await AsyncStorage.getItem(STORAGE_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    const now = Date.now();
                    const restored = {};
                    Object.entries(parsed).forEach(([id, timer]) => {
                        if (!timer) return;
                        // Stopped timers piled up forever (every task of every work order ever
                        // timed) and made every persist heavier; drop the long-finished ones.
                        if (timer.status === 'idle') {
                            const lastSegment = (timer.segments || [])[(timer.segments || []).length - 1];
                            const finishedAt = Number(lastSegment?.end) || Number(timer.startedAt) || 0;
                            if (finishedAt && now - finishedAt > IDLE_TIMER_RETENTION_MS) return;
                        }
                        // A running timer needs no catching up: its open segment keeps its real start,
                        // so the live value is already right however long the app was closed.
                        restored[id] = migrateLoadedTimer(timer, now);
                    });
                    setTimers(restored);
                }
            } catch (e) {
                console.warn('TimeTracking: Failed to load', e);
            }
            loadedRef.current = true;
        };
        load();
    }, []);

    // Persist whenever a timer really changes (start/pause/stop/…). The 1 s repaint below
    // changes no timer, so there is no per-second write any more.
    useEffect(() => {
        if (!loadedRef.current) return;
        AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(timers)).catch(() => { });
    }, [timers]);

    // Repaint every second while a clock is open, and the instant the app returns to the foreground
    // (the wall clock kept running while the phone was locked). This only re-reads `Date.now()`.
    const hasLiveTimer = useMemo(() => Object.values(timers).some(isLive), [timers]);
    useEffect(() => {
        if (!hasLiveTimer) return undefined;

        setNowMs(Date.now());
        const handle = setInterval(() => setNowMs(Date.now()), 1000);
        const subscription = AppState.addEventListener('change', (next) => {
            if (next === 'active') setNowMs(Date.now());
        });

        return () => {
            clearInterval(handle);
            subscription.remove();
        };
    }, [hasLiveTimer]);

    // What consumers see: banked time + the live length of the open segment.
    const liveTimers = useMemo(() => {
        const live = {};
        Object.keys(timers).forEach((id) => {
            live[id] = withLiveElapsed(timers[id], nowMs);
        });
        return live;
    }, [timers, nowMs]);

    const startTimer = useCallback((worksheetId, taskId, taskName) => {
        haptics.medium();
        const now = Date.now();
        setTimers(prev => ({
            ...prev,
            [taskId]: startLocalTimer(prev[taskId], { worksheetId, taskId, taskName }, now),
        }));
        ServiceModuleToast.success(i18n.t('repair_timer_started'), taskName);
    }, []);

    const pauseTimer = useCallback((taskId) => {
        const now = Date.now();
        setTimers(prev => {
            const next = pauseLocalTimer(prev[taskId], now);
            return next === prev[taskId] ? prev : { ...prev, [taskId]: next };
        });
        ServiceModuleToast.info(i18n.t('repair_timer_paused'));
    }, []);

    const resumeTimer = useCallback((taskId) => {
        const now = Date.now();
        setTimers(prev => {
            const next = resumeLocalTimer(prev[taskId], now);
            return next === prev[taskId] ? prev : { ...prev, [taskId]: next };
        });
        ServiceModuleToast.success(i18n.t('repair_timer_resumed'));
    }, []);

    const startBreak = useCallback((taskId, reason = '') => {
        const now = Date.now();
        setTimers(prev => {
            const next = startLocalBreak(prev[taskId], now, reason);
            return next === prev[taskId] ? prev : { ...prev, [taskId]: next };
        });
        ServiceModuleToast.info(i18n.t('repair_timer_break_started'), reason);
    }, []);

    const endBreak = useCallback((taskId) => {
        const now = Date.now();
        setTimers(prev => {
            const next = endLocalBreak(prev[taskId], now);
            return next === prev[taskId] ? prev : { ...prev, [taskId]: next };
        });
        ServiceModuleToast.success(i18n.t('repair_timer_break_ended'));
    }, []);

    const stopTimer = useCallback((taskId) => {
        haptics.medium();
        const now = Date.now();
        setTimers(prev => {
            const next = stopLocalTimer(prev[taskId], now);
            return next === prev[taskId] ? prev : { ...prev, [taskId]: next };
        });
        ServiceModuleToast.success(i18n.t('repair_timer_stopped'));
    }, []);

    const removeTimer = useCallback((taskId) => {
        setTimers(prev => {
            const next = { ...prev };
            delete next[taskId];
            return next;
        });
    }, []);

    const getActiveTimers = useCallback(() => {
        return Object.values(liveTimers).filter(
            t => t.status === 'running' || t.status === 'paused' || t.status === 'break'
        );
    }, [liveTimers]);

    const getTimer = useCallback((taskId) => {
        return liveTimers[taskId] || null;
    }, [liveTimers]);

    const startAdHocTimer = useCallback((taskName) => {
        const taskId = `adhoc-${Date.now()}`;
        startTimer(null, taskId, taskName);
        return taskId;
    }, [startTimer]);

    // Memoize the context value. Every callback below is already useCallback-
    // stable, so the value ref changes only when the live timers change — consumers no
    // longer re-render on unrelated provider re-renders. Deps list every member
    // (getActiveTimers/getTimer already fold `liveTimers` in), so the live 1s repaint
    // still propagates exactly as before.
    const value = useMemo(() => ({
        timers: liveTimers,
        startTimer,
        startAdHocTimer,
        pauseTimer,
        resumeTimer,
        startBreak,
        endBreak,
        stopTimer,
        removeTimer,
        getActiveTimers,
        getTimer,
    }), [
        liveTimers,
        startTimer,
        startAdHocTimer,
        pauseTimer,
        resumeTimer,
        startBreak,
        endBreak,
        stopTimer,
        removeTimer,
        getActiveTimers,
        getTimer,
    ]);

    return (
        <TimeTrackingContext.Provider value={value}>
            {children}
        </TimeTrackingContext.Provider>
    );
};

export const useTimeTracking = () => {
    const ctx = useContext(TimeTrackingContext);
    if (!ctx) {
        throw new Error('useTimeTracking must be used within TimeTrackingProvider');
    }
    return ctx;
};

// Utility: format milliseconds to display string
export const formatElapsed = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    if (hours > 0) {
        return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    }
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

export default TimeTrackingContext;
